import { FunctionComponent, memo, useCallback, useContext, useMemo, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ConfigContext } from '../../../../../context';
import {
  Story,
  storyCardApi,
  StoryCardTeaserImageStyle,
  StoryCardType,
} from '../../../../../services';
import { GalleryItem } from '../../../../models';
import {
  getApplePodcastMetaData,
  getCssVar,
  isAdminLayout,
  isApplePodcastUrl,
} from '../../../../utils';
import { IconLabel } from '../../../IconLabel';
import { Select } from '../../../Select';
import { HEADLINE_MAX_LENGTH, SOURCE_MAX_LENGTH } from '../../ShortPost';
import { FullPostFields, LinkPostFields, PostType, ShortPostFields } from '../CreateCardModal';

import classes from './CreateCardHeader.module.scss';

interface CreateCardHeaderProps {
  postInStories: Story[];
  postInStoryId: number;
  setPostInStoryId: (id: number) => void;
  postInStorageKey: string;
  editMode: boolean;
  isFullPost: boolean;
  isLinkPost: boolean;
  isIgdbPost: boolean;
  isCategoryCard: boolean;
  isTemplateEditor: boolean;
  loading: boolean;
  linkPostFieldsUrl: string;
  postInStoryTeaserStyle: StoryCardTeaserImageStyle;
  setPostType: (type: PostType) => void;
  setShortPostFields: (shortPostFields: ShortPostFields) => void;
  setFullPostFields: (fullPostFields: FullPostFields) => void;
  setLinkPostFields: (linkPostFields: LinkPostFields) => void;
  setLinkPostPrevUrl: (url: string) => void;
  setFullPostTextEditorOpen: (isOpen: boolean) => void;
  linkPostType?: StoryCardType;
}

export const CreateCardHeader: FunctionComponent<CreateCardHeaderProps> = memo(
  ({
    postInStories,
    postInStoryId,
    setPostInStoryId,
    postInStorageKey,
    editMode,
    isFullPost,
    isLinkPost,
    isIgdbPost,
    isCategoryCard,
    isTemplateEditor,
    loading,
    linkPostFieldsUrl,
    postInStoryTeaserStyle,
    setPostType,
    setShortPostFields,
    setFullPostFields,
    setLinkPostFields,
    setLinkPostPrevUrl,
    setFullPostTextEditorOpen,
    linkPostType,
  }) => {
    const { t } = useTranslation();

    const { excludeLongPost } = useContext(ConfigContext).config.elements.addContent;

    const { storyCardParseUrl, storyCardParseUrlAsNative, storyCardParseUrlAsPost } =
      storyCardApi.endpoints;

    const [cardParseUrl] = storyCardParseUrl.useMutation();

    const [cardParseUrlAsNative] = storyCardParseUrlAsNative.useMutation();

    const [cardParseUrlAsPost] = storyCardParseUrlAsPost.useMutation();

    const [convertBackToLinkPost, setConvertBackToLinkPost] = useState(false);

    const hideConvertToLongPost = useMemo(
      () => !isAdminLayout() && excludeLongPost,
      [excludeLongPost]
    );

    const postInStoryDisabled = useMemo(() => postInStories.length <= 1, [postInStories.length]);

    const onPostInStoryChange = useCallback(
      (id: number) => {
        if (!editMode) {
          localStorage.setItem(postInStorageKey, id.toString());
        }
        setPostInStoryId(id);
      },
      [editMode, postInStorageKey, setPostInStoryId]
    );

    const parseUrlAsNativeHandler = useCallback(
      async (toastId: string | number) => {
        const fields = await cardParseUrlAsNative({ url: linkPostFieldsUrl }).unwrap();

        toast.dismiss(toastId);

        if (fields) {
          setConvertBackToLinkPost(true);
          setShortPostFields({ gallery: [], ...fields });
          setPostType(PostType.SHORT);
          return;
        }

        toast.error(t('addContent.convert-to-short-post-error'), { delay: 1000 });
      },
      [cardParseUrlAsNative, linkPostFieldsUrl, setPostType, setShortPostFields, t]
    );

    const convertToShortPost = useCallback(async () => {
      const toastId = toast.loading(t('addContent.converting-to-short-post'));

      if (!isApplePodcastUrl(linkPostFieldsUrl)) {
        parseUrlAsNativeHandler(toastId);
        return;
      }

      const { source, headline, description, audioLink, imageLink } = await getApplePodcastMetaData(
        linkPostFieldsUrl
      );

      if (!audioLink) {
        parseUrlAsNativeHandler(toastId);
        return;
      }

      const gallery: GalleryItem[] = [];

      try {
        const parsedAudioData = await cardParseUrl({ url: audioLink }).unwrap();

        if (!parsedAudioData) {
          parseUrlAsNativeHandler(toastId);
          return;
        }

        const parsedImageData = imageLink ? await cardParseUrl({ url: imageLink }).unwrap() : null;

        gallery.push({
          ...(parsedAudioData.gallery[0] ?? {}),
          ...(parsedImageData?.gallery?.[0] ?? {}),
          title: '',
        } as GalleryItem);
      } catch (_) {
        toast.error(t('fileUpload.error'));
      } finally {
        toast.dismiss(toastId);
      }

      setConvertBackToLinkPost(true);
      setShortPostFields({
        sourceName: source.slice(0, SOURCE_MAX_LENGTH),
        headline: headline.slice(0, HEADLINE_MAX_LENGTH),
        subHeadline: '',
        text: description,
        gallery,
      });
      setPostType(PostType.SHORT);
    }, [
      cardParseUrl,
      linkPostFieldsUrl,
      parseUrlAsNativeHandler,
      setPostType,
      setShortPostFields,
      t,
    ]);

    const convertToFullPost = useCallback(async () => {
      const toastId = toast.loading(t('addContent.converting-to-full-post'));

      const fields = await cardParseUrlAsPost({ url: linkPostFieldsUrl }).unwrap();

      toast.dismiss(toastId);

      if (fields) {
        setConvertBackToLinkPost(true);

        setFullPostFields({
          ...fields,
          headline: '',
          styles: { teaserImageStyle: postInStoryTeaserStyle },
        } as FullPostFields);

        setPostType(PostType.FULL);

        return;
      }

      toast.error(t('addContent.convert-to-full-post-error'), { delay: 1000 });
    }, [
      cardParseUrlAsPost,
      linkPostFieldsUrl,
      postInStoryTeaserStyle,
      setConvertBackToLinkPost,
      setFullPostFields,
      setPostType,
      t,
    ]);

    const convertBackToLinkPostHandler = useCallback(() => {
      setConvertBackToLinkPost(false);
      setPostType(PostType.LINK);
    }, [setConvertBackToLinkPost, setPostType]);

    const resetLinkPostHandler = useCallback(() => {
      setLinkPostFields({ url: '' });
      setLinkPostPrevUrl('');
    }, [setLinkPostFields, setLinkPostPrevUrl]);

    const buttonCommonProps = useMemo(() => {
      return {
        iconSize: 18,
        disabled: loading,
        color: getCssVar('--base-link-text-color'),
        hoverColor: getCssVar('--base-link-text-hover-color'),
      };
    }, [loading]);

    const renderLinkPostConvert = useMemo(
      () => Boolean(isLinkPost && linkPostType === StoryCardType.ARTICLE),
      [isLinkPost, linkPostType]
    );

    const renderLinkPostReset = useMemo(
      () => Boolean(!editMode && isLinkPost && linkPostType),
      [editMode, isLinkPost, linkPostType]
    );

    const renderIgdbReset = useMemo(
      () => Boolean(!editMode && isIgdbPost && linkPostType),
      [editMode, isIgdbPost, linkPostType]
    );

    const buttons = useMemo(() => {
      if (
        !renderLinkPostConvert &&
        !renderLinkPostReset &&
        !renderIgdbReset &&
        !convertBackToLinkPost &&
        !isFullPost
      ) {
        return null;
      }

      return (
        <div className={classes['header__buttons']}>
          {renderLinkPostConvert && (
            <>
              <IconLabel
                iconId={'editorial'}
                {...(!isMobileOnly && { label: t('addContent.link-post-convert-to-short-post') })}
                ariaLabel={t('addContent.link-post-convert-to-short-post')}
                onClick={convertToShortPost}
                {...buttonCommonProps}
              />
              {!hideConvertToLongPost && (
                <IconLabel
                  iconId={'article'}
                  {...(!isMobileOnly && { label: t('addContent.link-post-convert-to-full-post') })}
                  ariaLabel={t('addContent.link-post-convert-to-full-post')}
                  onClick={convertToFullPost}
                  {...buttonCommonProps}
                />
              )}
            </>
          )}
          {renderLinkPostReset && (
            <IconLabel
              iconId={'add-link'}
              {...(!isMobileOnly && { label: t('addContent.link-post-reset-label') })}
              ariaLabel={t('addContent.link-post-reset-label')}
              onClick={resetLinkPostHandler}
              {...buttonCommonProps}
            />
          )}
          {renderIgdbReset && (
            <IconLabel
              iconId={'add-link'}
              {...(!isMobileOnly && { label: t('addContent.igdb-post-reset-label') })}
              ariaLabel={t('addContent.igdb-post-reset-label')}
              onClick={resetLinkPostHandler}
              {...buttonCommonProps}
            />
          )}
          {isFullPost && (
            <IconLabel
              iconId={'edit'}
              {...(!isMobileOnly && { label: t('addContent.full-post-open-text-editor') })}
              ariaLabel={t('addContent.full-post-open-text-editor')}
              onClick={() => setFullPostTextEditorOpen(true)}
              {...buttonCommonProps}
            />
          )}
          {convertBackToLinkPost && (
            <IconLabel
              iconId={'redo'}
              {...(!isMobileOnly && { label: t('addContent.convert-back-to-link-post') })}
              ariaLabel={t('addContent.convert-back-to-link-post')}
              onClick={convertBackToLinkPostHandler}
              {...buttonCommonProps}
            />
          )}
        </div>
      );
    }, [
      buttonCommonProps,
      convertBackToLinkPost,
      convertBackToLinkPostHandler,
      convertToFullPost,
      convertToShortPost,
      hideConvertToLongPost,
      isFullPost,
      renderIgdbReset,
      renderLinkPostConvert,
      renderLinkPostReset,
      resetLinkPostHandler,
      setFullPostTextEditorOpen,
      t,
    ]);

    if (isCategoryCard || (isTemplateEditor && !buttons)) {
      return null;
    }

    return (
      <div className={classes['header']}>
        {!isTemplateEditor && (
          <div className={classes['header__post-in']}>
            <span className={classes['header__label']}>
              {t(`common.${editMode ? 'edit' : 'create'}-post-in`)}
            </span>
            <Select
              className={classes['header__select']}
              items={postInStories}
              onChange={(id) => onPostInStoryChange(id as number)}
              selectedItemId={postInStoryId}
              listMaxHeightRem={10.5}
              listMinWidthRem={16}
              disabled={postInStoryDisabled}
              noSidePadding={postInStoryDisabled}
              noBorder
            />
          </div>
        )}
        {buttons}
      </div>
    );
  }
);
